// @flow
import { useRef } from 'react';
import { Controller } from 'react-hook-form/dist/react-hook-form.ie11';
import Select, { SelectWrapper } from '@tablekit/select';
import PhoneInput from '@tablekit/phone-input';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import Checkbox from '@tablekit/checkbox';
import { SPACING } from '@tablekit/theme';
import Input from '@tablekit/input';
import Button from '@tablekit/button';
import { LAPTOP_BREAKPOINT, MOBILE_BREAKPOINT } from 'utils/theme';
import stringValidator from 'yup/lib/string';
import type {
  BlokComponentType,
  FormFieldType,
  FormRequestType,
  FormSelectType
} from '../types';
import componentKeys from '../../componentKeys';
import type { I18nCountriesType } from './types';

/* eslint-disable react/no-unused-prop-types */
// shared prop type doesn't correctly do unused props detection
type ContentItemType = {
  country: string,
  form: *,
  i18nCountries: I18nCountriesType,
  isFormDirty: boolean,
  isInvalid: boolean,
  isLoading: boolean
};
/* eslint-enable react/no-unused-prop-types */

export const MIN_INPUT_WIDTH = '360px';

const validations = {
  email: stringValidator().email(),
  url: stringValidator().url()
};

const ColumnCheckbox = styled(Checkbox)`
  ${({ isMessageHidden }) =>
    isMessageHidden
      ? css`
          margin-bottom: ${SPACING.L4};
        `
      : css`
          margin-bottom: ${SPACING.L6};
        `};
  @media ${LAPTOP_BREAKPOINT} {
    // this isn't exact, but it's here to fix IE11 so it should be OK
    max-width: ${MIN_INPUT_WIDTH};
  }
`;

const columnInputStyles = css`
  flex: 1 1 auto;
  height: auto;
  * {
    max-width: 100%;
  }
`;

const ColumnInput = styled(Input)`
  ${columnInputStyles};
`;

const ColumnPhoneInput = styled(PhoneInput)`
  ${columnInputStyles};
`;

export const SubmitButton = styled(Button)`
  justify-self: flex-end;
  align-self: flex-end;
`;

export const FormButton = styled(Button)`
  @media ${MOBILE_BREAKPOINT} {
    text-transform: uppercase;
  }
`;

const sharedSelectOptions = {
  shouldFitContainer: true,
  isSearchable: false,
  getOptionValue: ({ value, label }) => {
    if (value && label) return `${label} (${value})`;
    if (value) return value;
    return label;
  },
  getOptionLabel: ({ value, label }) => label || value
};

const ControllerSelect = ({ value, options, ...props }: *) => {
  const foundValue = options.find((option) => option.value === value);
  return <Select value={foundValue} options={options} {...props} />;
};

export const ContentItemRenderers = {
  [componentKeys.FORM_SELECT]: ({
    form,
    isInvalid,
    item: {
      name,
      label,
      placeholder,
      is_required: isRequired,
      validation_message: validationMessage,
      options
    }
  }: ContentItemType & { item: FormSelectType }) => (
    <SelectWrapper
      label={label}
      isRequired={isRequired}
      message={isInvalid ? validationMessage : undefined}
      isInvalid={isInvalid}
    >
      <Controller
        as={ControllerSelect}
        control={form.control}
        {...sharedSelectOptions}
        rules={{ required: isRequired }}
        options={options}
        name={name}
        placeholder={placeholder}
        onChange={([selectedOption]) =>
          sharedSelectOptions.getOptionValue(selectedOption)
        }
      />
    </SelectWrapper>
  ),
  [componentKeys.FORM_REQUEST_TYPE]: ({
    form,
    item: {
      label,
      placeholder,
      validation_message: validationMessage,
      ...optionLabels
    }
  }: ContentItemType & { item: FormRequestType }) => {
    const options = Object.keys(optionLabels).reduce((result, labelKey) => {
      if (labelKey.indexOf('_label') >= 0 && optionLabels[labelKey]) {
        result.push({
          label: optionLabels[labelKey],
          value: labelKey.split('_').slice(0, 2).join('-')
        });
      }
      return result;
    }, []);
    const formIdIsInvalid =
      (form.formState.isSubmitted ||
        (form.formState.touched && form.formState.touched.form_id)) &&
      form.errors.form_id;
    return (
      <SelectWrapper
        label={label}
        isRequired
        message={formIdIsInvalid ? validationMessage : undefined}
        isInvalid={formIdIsInvalid}
      >
        <Controller
          as={ControllerSelect}
          control={form.control}
          {...sharedSelectOptions}
          rules={{ required: true }}
          name="form_id"
          placeholder={placeholder}
          options={options}
          onChange={([selectedOption]) => (selectedOption || {}).value}
        />
      </SelectWrapper>
    );
  },
  [componentKeys.FORM_FIELD]: ({
    form,
    isInvalid,
    isLoading,
    isFormDirty,
    country,
    i18nCountries,
    item: {
      name,
      label,
      placeholder,
      type,
      is_required: isRequired,
      validation_message: validationMessage
    }
  }: ContentItemType & { item: FormFieldType }) => {
    const isValid = useRef();
    let validateFunction;
    switch (type) {
      case 'phone':
        return (
          <Controller
            as={ColumnPhoneInput}
            control={form.control}
            label={label}
            placeholder={placeholder}
            name={name}
            isRequired={isRequired}
            rules={{
              required: isRequired,
              validate: () => isValid.current
            }}
            onChange={([value, metadata]) => {
              if (!metadata.isValid) {
                form.setError(name, 'format', 'Invalid Phone Number');
              } else {
                form.clearError(name);
              }
              isValid.current = metadata.isValid;
              return value;
            }}
            isInvalid={isInvalid}
            message={isInvalid ? validationMessage : undefined}
            i18nCountries={i18nCountries}
            defaultCountry={country}
            shouldFitContainer
          />
        );
      case 'checkbox':
        return (
          <ColumnCheckbox
            name={name}
            isRequired={isRequired}
            ref={form.register({
              required: isRequired
            })}
            isInvalid={isInvalid}
            message={isInvalid ? validationMessage : undefined}
          >
            {typeof label === 'string' ? (
              // this is for supporting deprecated modal types that are still in use
              <span dangerouslySetInnerHTML={{ __html: label }} />
            ) : (
              label
            )}
          </ColumnCheckbox>
        );
      case 'submit':
        return (
          <FormButton
            shouldFitContainer
            isLoading={isLoading}
            isDisabled={!isFormDirty}
            type="submit"
          >
            {label}
          </FormButton>
        );
      default:
        // $FlowFixMe
        if (validations[type]) {
          validateFunction = (value) => {
            try {
              // $FlowFixMe
              validations[type].validateSync(value);
              return true;
            } catch (error) {
              return false;
            }
          };
        }
        return (
          <ColumnInput
            type={type}
            label={label}
            placeholder={placeholder}
            name={name}
            isRequired={isRequired}
            ref={form.register({
              required: isRequired,
              validate: validateFunction
            })}
            isInvalid={isInvalid}
            message={isInvalid ? validationMessage : undefined}
          />
        );
    }
  }
};

export function ContentItem(
  props: ContentItemType & {
    // due to this being essentially a compound type we are forced to use `any` here
    // otherwise the types conflict
    item: BlokComponentType<any>
  }
) {
  const {
    item: { component }
  } = props;
  const renderer = ContentItemRenderers[component];
  if (renderer) {
    return renderer(props);
  }
  return <div>Unknown/Unsupported Component {component}</div>;
}
